import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { LoginUserOption, SedePayload, UserPayload } from '../models';
import { ApiService } from './api.service';
import { ToastService } from './toast.service';
import { environment } from '../../../environments';

@Injectable({ providedIn: 'root' })
export class AuthService {
  user: UserPayload | null = null;
  pendingUser: UserPayload | null = null;
  private sessionExpiredHandled = false;

  constructor(private api: ApiService, private router: Router, private toast: ToastService) {
    const cache = localStorage.getItem('auth');

    if (cache) {
      try {
        this.user = JSON.parse(cache);
      } catch {
        this.clear();
      }
    }
  }

  async loginUsers(): Promise<LoginUserOption[]> {
    const resp = await firstValueFrom(this.api.get<LoginUserOption[]>('/auth/login-users'));

    if (!resp.success) {
      throw new Error(this.getApiMessage(resp, 'No se pudieron cargar los usuarios'));
    }

    return resp.result || [];
  }

  async login(email: string, password: string): Promise<UserPayload> {
    const resp = await firstValueFrom(
      this.api.post<UserPayload>('/auth/login', { email, password })
    );

    if (!resp.success) {
      throw new Error(this.getApiMessage(resp, 'Login inválido'));
    }

    const data = resp.result as UserPayload;

    // La sesión queda pendiente hasta que el usuario seleccione explícitamente
    // el negocio/sede donde va a trabajar. Así evitamos que el admin cree
    // productos, compras o ventas en la sede equivocada.
    this.pendingUser = data;

    return data;
  }

  async selectSede(sede_id: number): Promise<UserPayload | null> {
    const source = this.pendingUser || this.user;

    if (!source?.token) {
      throw new Error('No hay sesión activa para seleccionar negocio/sede');
    }

    // Para el login pendiente usamos token temporal; para cambio desde el header
    // usamos el token actual. En ambos casos el interceptor leerá localStorage.
    localStorage.setItem('auth', JSON.stringify(source));

    const resp = await firstValueFrom(
      this.api.post<UserPayload>('/auth/select-sede', { sede_id })
    );

    if (!resp.success) {
      throw new Error(this.getApiMessage(resp, 'No se pudo seleccionar la sede'));
    }

    this.setSession(resp.result as UserPayload);
    return this.user;
  }

  setSession(data: UserPayload): void {
    this.user = data;
    this.pendingUser = null;
    localStorage.setItem('auth', JSON.stringify(this.user));
    this.sessionExpiredHandled = false;
  }

  clear(): void {
    this.user = null;
    this.pendingUser = null;
    localStorage.removeItem('auth');
  }

  logout(): void {
    this.clear();
    this.router.navigateByUrl('/login');
  }

  expireSession(): void {
    if (this.sessionExpiredHandled) return;

    this.sessionExpiredHandled = true;
    this.clear();
    this.toast.warning('Sesión expirada', 'Por favor inicia sesión nuevamente.');
    this.router.navigateByUrl('/login');
  }

  token(): string {
    return this.user?.token || this.pendingUser?.token || '';
  }

  hasRole(roles: string[]): boolean {
    const role = (this.user?.role || '').toUpperCase();
    return roles.map((item) => item.toUpperCase()).includes(role);
  }

  isAdmin(): boolean {
    return this.hasRole(['ADMIN']);
  }

  sedes(): SedePayload[] {
    return this.user?.sedes || this.pendingUser?.sedes || [];
  }

  activeSedeLabel(): string {
    return this.user?.sede_nombre || 'Sin negocio seleccionado';
  }

  activeSedeNitLabel(): string {
    return this.user?.sede_nit ? `NIT ${this.user.sede_nit}` : 'Sin NIT registrado';
  }

  activeSedeDetail(): string {
    if (!this.user?.sede_nombre) return 'Debes seleccionar un negocio al iniciar sesión.';
    const parts = [
      this.user.sede_razon_social || this.user.empresa_nombre,
      this.user.sede_codigo ? `Código ${this.user.sede_codigo}` : '',
    ].filter(Boolean);
    return parts.join(' · ');
  }

  activeSedeLogo(): string {
    return this.normalizeAssetUrl(this.user?.sede_logo_url) || 'assets/img/logo_ospm.png';
  }

  normalizeAssetUrl(url?: string | null): string {
    const value = String(url || '').trim();
    if (!value) return '';
    if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:')) return value;
    if (value.startsWith('/')) {
      const base = (environment.socketUrl || environment.apiBaseUrl.replace(/\/api\/?$/, '')).replace(/\/$/, '');
      return `${base}${value}`;
    }
    return value;
  }

  updateActiveSedeLogo(logoUrl: string): void {
    if (!this.user) return;
    this.user = { ...this.user, sede_logo_url: logoUrl };
    localStorage.setItem('auth', JSON.stringify(this.user));
  }

  private getApiMessage(resp: unknown, fallback: string): string {
    const response = resp as {
      result?: { message?: string } | null;
      message?: string;
      status?: string;
    };

    return (
      response?.result?.message ||
      response?.message ||
      response?.status ||
      fallback
    );
  }
}
